// Zwei-Faktor einrichten (Schritte: QR-Code scannen, ersten Code eingeben, Wiederherstellungscodes sichern).
// Benutzt von Einstellungen › Sicherheit und von der Pflichtseite, wenn die Organisation Zwei-Faktor verlangt.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { nutzerVerwaltungApi } from "../api/nutzerVerwaltung";
import { FehlerBox, fehlerInfo } from "../components/Fehler";
import { Laden } from "../components/Bausteine";
import { QrBild } from "./QrBild";
import { T8C } from "./texte";
import { Wiederherstellungscodes } from "./Wiederherstellungscodes";

const T = T8C.sicherheit.einrichten;

/** `ABCDEFGH…` -> `ABCD EFGH …` */
export function geheimnisGruppiert(g: string): string {
  return g.replace(/(.{4})/g, "$1 ").trim();
}

interface Props {
  /** nach dem Einschalten und nachdem die Codes bestätigt sind */
  onFertig: () => void;
  onAbbrechen?: () => void;
}

export function ZweiFaktorEinrichten({ onFertig, onAbbrechen }: Props) {
  const [daten, setDaten] = useState<{ geheimnis: string; otpauth_url: string } | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [code, setCode] = useState("");
  const [formFehler, setFormFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const gestartet = useRef(false);

  const starten = async () => {
    setDaten(null);
    setFehler(null);
    setCode("");
    setFormFehler(null);
    try {
      setDaten(await nutzerVerwaltungApi.zweiFaktorEinrichten());
    } catch (e) {
      setFehler(e);
    }
  };

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    void starten();
  }, []);

  const bestaetigen = async (e: FormEvent) => {
    e.preventDefault();
    setLaeuft(true);
    setFormFehler(null);
    try {
      const r = await nutzerVerwaltungApi.zweiFaktorBestaetigen(code.replace(/\s/g, ""));
      setCodes(r.wiederherstellungscodes);
    } catch (err) {
      setFormFehler(fehlerInfo(err).text);
      setCode("");
    } finally {
      setLaeuft(false);
    }
  };

  if (codes) return <Wiederherstellungscodes codes={codes} onFertig={onFertig} />;
  if (fehler) return <FehlerBox fehler={fehler} onNochmal={() => void starten()} />;
  if (!daten) return <Laden />;

  return (
    <form className="nv-einrichten" onSubmit={(e) => void bestaetigen(e)}>
      <section className="nv-einrichten__schritt">
        <h3>{T.schritt1}</h3>
        <div className="nv-qr">
          <div className="nv-qr__bild">
            <QrBild text={daten.otpauth_url} titel={T.titel} />
          </div>
          <div className="nv-qr__text">
            <p className="nv-hilfe">{T.schritt1Alt}</p>
            <div>
              <span className="nv-leise">{T.schluessel}: </span>
              <span className="nv-geheimnis">{geheimnisGruppiert(daten.geheimnis)}</span>
            </div>
            <p className="nv-hilfe">{T.zeitbasiert}</p>
            <div className="nv-zeile">
              <a className="nv-linkknopf" href={daten.otpauth_url}>
                {T.appOeffnen}
              </a>
              <button type="button" className="nv-linkknopf" onClick={() => void starten()}>
                {T.neu}
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="nv-einrichten__schritt">
        <h3>{T.schritt2}</h3>
        {formFehler ? (
          <div className="hinweis hinweis--fehler" role="alert">
            {formFehler}
          </div>
        ) : null}
        <label className="feld">
          <span className="feld__label">{T.code}</span>
          <input
            className="eingabe nv-codefeld"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            pattern="[0-9 ]*"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
            required
          />
        </label>
        <div className="nv-zeile">
          <button type="submit" className="knopf knopf--primaer" disabled={laeuft || code.replace(/\s/g, "").length !== 6}>
            {laeuft ? T.laeuft : T.bestaetigen}
          </button>
          {onAbbrechen ? (
            <button type="button" className="knopf knopf--leise" onClick={onAbbrechen} disabled={laeuft}>
              {T.abbrechen}
            </button>
          ) : null}
        </div>
      </section>
    </form>
  );
}
